import { ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Link,
  useDisclosure,
  useColorModeValue,
  Stack,
  Text,
  Container,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { HamburgerIcon, CloseIcon, SettingsIcon } from '@chakra-ui/icons';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../services/supabase';

interface AppLayoutProps {
  children: ReactNode;
}

const NavLink = ({ children, to }: { children: ReactNode; to: string }) => (
  <Link
    as={RouterLink}
    px={2}
    py={1}
    rounded="md"
    _hover={{
      textDecoration: 'none',
      bg: useColorModeValue('gray.200', 'gray.700'),
    }}
    to={to}
  >
    {children}
  </Link>
);

const AppLayout = ({ children }: AppLayoutProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, signOut } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine if we're on the dashboard page
  const isDashboardPage = location.pathname === '/dashboard';
  
  const handleSignOut = async () => {
    try {
      // First attempt to sign out via the store function
      await signOut();
      
      // Navigate using React Router instead of window.location
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Fallback: If the signOut function fails, try direct Supabase signOut
      try {
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
      } catch (fallbackError) {
        console.error('Fallback sign out also failed:', fallbackError);
        
        // Last resort: Clear state manually and navigate
        useAuthStore.setState({ user: null, session: null, loading: false });
        navigate('/login', { replace: true });
      }
    }
  };

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      <Box bg={useColorModeValue('white', 'gray.800')} px={4} boxShadow="sm">
        <Flex h={16} alignItems="center" justifyContent="space-between">
          <IconButton
            size="md"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Open Menu"
            display={{ md: 'none' }}
            onClick={isOpen ? onClose : onOpen}
          />
          <HStack spacing={8} alignItems="center">
            <Box fontWeight="bold" fontSize="xl">
              <Link
                as={RouterLink}
                to="/dashboard"
                _hover={{ textDecoration: 'none' }}
              >
                Billable Hours
              </Link>
            </Box>
            <HStack as="nav" spacing={4} display={{ base: 'none', md: 'flex' }}>
              {/* Show Dashboard link only when not on the dashboard page */}
              {!isDashboardPage && <NavLink to="/dashboard">Dashboard</NavLink>}
            </HStack>
          </HStack>
          <Flex alignItems="center">
            <Menu>
              <MenuButton
                as={Button}
                rounded="full"
                variant="link"
                cursor="pointer"
                minW={0}
              >
                <Avatar size="sm" name={user?.email} />
              </MenuButton>
              <MenuList>
                <Text px={3} py={2} color="gray.500">
                  {user?.email}
                </Text>
                <MenuDivider />
                <MenuItem as={RouterLink} to="/settings" icon={<SettingsIcon />}>
                  Settings
                </MenuItem>
                <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        {isOpen ? (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as="nav" spacing={4}>
              {/* Show Dashboard link only when not on the dashboard page */}
              {!isDashboardPage && <NavLink to="/dashboard">Dashboard</NavLink>}
            </Stack>
          </Box>
        ) : null}
      </Box>

      <Container maxW="container.xl" py={6}>
        {children}
      </Container>
    </Box>
  );
};

export default AppLayout;